import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';

interface UserTraits {
  email?: string;
  name?: string;
  userType?: 'user' | 'partner' | 'admin';
  planTier?: string;
  partnerId?: string;
  [key: string]: unknown;
}

export const usePostHog = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isEnabled = Boolean(import.meta.env.VITE_POSTHOG_KEY) && !isAdminRoute;

  const captureEvent = useCallback((eventName: string, properties?: Record<string, unknown>) => {
    if (!isEnabled || !posthog.__loaded) return;
    
    posthog.capture(eventName, {
      ...properties,
      $current_url: window.location.href,
    });
  }, [isEnabled]);

  const identifyUser = useCallback((userId: string, traits?: UserTraits) => {
    if (!posthog.__loaded) return;
    
    // Don't identify admin users
    if (traits?.userType === 'admin') return;
    
    posthog.identify(userId, {
      email: traits?.email,
      name: traits?.name,
      user_type: traits?.userType,
      plan_tier: traits?.planTier,
      partner_id: traits?.partnerId,
      ...traits,
    });
  }, []);

  const resetUser = useCallback(() => {
    if (!posthog.__loaded) return;
    posthog.reset();
  }, []);

  const setUserProperties = useCallback((properties: Record<string, unknown>) => {
    if (!isEnabled || !posthog.__loaded) return;
    posthog.people.set(properties);
  }, [isEnabled]);

  return {
    captureEvent,
    identifyUser,
    resetUser,
    setUserProperties,
    isEnabled,
  };
};

// Standalone functions for use outside React components
export const capturePostHogEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (!import.meta.env.VITE_POSTHOG_KEY || !posthog.__loaded) return;
  if (window.location.pathname.startsWith('/admin')) return;
  
  posthog.capture(eventName, {
    ...properties,
    $current_url: window.location.href,
  });
};

export const identifyPostHogUser = (userId: string, traits?: UserTraits) => {
  if (!posthog.__loaded) return;
  if (traits?.userType === 'admin') return;
  
  posthog.identify(userId, {
    email: traits?.email,
    name: traits?.name,
    user_type: traits?.userType,
    plan_tier: traits?.planTier,
    partner_id: traits?.partnerId,
    ...traits,
  });
};

export const resetPostHogUser = () => {
  if (!posthog.__loaded) return;
  posthog.reset();
};
